import { get, IHttpRequestConfig, put } from "@/browser";
import { applyHeaders, extractDataFromResponse, parseResponse } from "@/core/api";
import { compose, IURLPageConfig, IURLSearchParams, IURLSortConfig } from "@/core/url";
import { NewsListPageable, NewsItem } from "@/data/public-profiles/types";
import { stringType } from "@/types/service";

import {
  IPartialPageablePublicProfileNews,
  IPublicProfile,
  IPublicProfileInfo,
  IPublicProfilesFollowedData,
  pageablePublicProfileNewsType,
  publicProfileFollowStatusType,
  publicProfileInfoType,
  publicProfileType,
} from "./types-ensure";

const API = {
  get: "/api/public/v1/public-profiles/{id}",
  getBySlug: "/api/public/v1/public-profiles/slug/{slug}",
  getInfoBySlug: "/api/public/public-profile/{slug}/followed",
  status: "/api/public-profile/{publicProfileId}/follow/status",
  follow: "/api/public-profile/{publicProfileId}/follow",
  unfollow: "/api/public-profile/{publicProfileId}/unfollow",
  followedList: "/api/public-profile/followed",
  getPublicProfileNewsList: "/api/public/v1/public-profiles/{publicProfileId}/news",
  getNewsItem: "/api/public/v1/public-profiles/news/{newsId}",
  getNewsList: "/api/public/v1/public-profiles/news",
  getEworkNewsList: "/api/public/v1/public-profiles/eworknews",
};

export interface IFetchPublicProfileParams {
  id: number;
}

export interface IFetchPublicProfileBySlugParams {
  slug: string;
}

export interface IFollowPublicProfileParams {
  publicProfileId: number;
  companyId?: number;
}

export const fetchPublicProfile = (params: IFetchPublicProfileParams, options: IHttpRequestConfig = {}) => {
  const { id, ...searchParams } = params;
  const url = compose(API.get, { id }, searchParams);
  return get<IPublicProfile>(url, options).then(extractDataFromResponse).then(publicProfileType);
};

export const fetchPublicProfileBySlug = (params: IFetchPublicProfileBySlugParams, options: IHttpRequestConfig = {}) => {
  const { slug, ...searchParams } = params;
  const url = compose(API.getBySlug, { slug }, searchParams);
  return get<IPublicProfile>(url, options).then(extractDataFromResponse).then(publicProfileType);
};

export const fetchPublicProfileInfoBySlug = (
  params: IFetchPublicProfileBySlugParams,
  options: IHttpRequestConfig = {},
) => {
  const { slug, ...searchParams } = params;
  const url = compose(API.getInfoBySlug, { slug }, searchParams);
  return get<IPublicProfileInfo>(url, options).then(extractDataFromResponse).then(publicProfileInfoType);
};

export const fetchPublicProfileFollowStatus = (
  params: IFollowPublicProfileParams,
  options: IHttpRequestConfig = {},
) => {
  const { publicProfileId, companyId, ...searchParams } = params;
  const url = compose(API.status, { publicProfileId }, searchParams);
  return get<IPublicProfile>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(stringType(""));
};

export const followPublicProfile = (params: IFollowPublicProfileParams, options: IHttpRequestConfig = {}) => {
  const { publicProfileId, companyId, ...searchParams } = params;
  const url = compose(API.follow, { publicProfileId }, searchParams);
  return put<IPublicProfile>(url, {}, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(publicProfileFollowStatusType);
};

export const unfollowPublicProfile = (params: IFollowPublicProfileParams, options: IHttpRequestConfig = {}) => {
  const { publicProfileId, companyId, ...searchParams } = params;
  const url = compose(API.unfollow, { publicProfileId }, searchParams);
  return put<IPublicProfile>(url, {}, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(publicProfileFollowStatusType);
};

export interface IFollowedPublicProfileParams {
  companyId?: number;
}

export const fetchFollowedPublicProfiles = (params: IFollowedPublicProfileParams, options: IHttpRequestConfig = {}) => {
  const { companyId, ...searchParams } = params;
  const url = compose(API.followedList, {}, searchParams);
  return get<IPublicProfilesFollowedData[]>(url, applyHeaders({ companyId }, options)).then(extractDataFromResponse);
};

export interface IFetchPublicProfileNewsListParams extends IURLSearchParams {
  publicProfileId: number;
  companyId?: number;
}

export const fetchPublicProfileNewsList = (
  params: IFetchPublicProfileNewsListParams,
  options: IHttpRequestConfig = {},
) => {
  const { companyId, publicProfileId, ...searchParams } = params;

  const url = compose(API.getPublicProfileNewsList, { publicProfileId }, searchParams);

  return get<IPartialPageablePublicProfileNews>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageablePublicProfileNewsType);
};

export interface FetchNewsItemParams extends Partial<IURLPageConfig>, Partial<IURLSortConfig> {
  newsId: number;
  companyId?: number;
}

export const fetchNewsItem = (params: FetchNewsItemParams, options?: IHttpRequestConfig) => {
  const { newsId, companyId, ...restParams } = params;

  const url = compose(API.getNewsItem, { newsId }, restParams);

  return get(url, applyHeaders({ companyId }, options)).then(parseResponse<NewsItem>(NewsItem));
};

export interface FetchNewsListParams extends Partial<IURLPageConfig>, Partial<IURLSearchParams> {}

export const fetchNewsList = (params: FetchNewsListParams, options?: IHttpRequestConfig) => {
  const { ...restParams } = params;

  const url = compose(API.getEworkNewsList, {}, restParams);

  return get(url, applyHeaders({}, options)).then(parseResponse<NewsListPageable>(NewsListPageable));
};
